import { looksLikeRuntimePermissionDenialMessage } from "@wakeloop/adapter-core";
const OPENCODE_SESSION_ID_PATHS = [
    ["sessionID"],
    ["sessionId"],
    ["session_id"],
    ["session", "id"],
    ["metadata", "sessionID"],
    ["metadata", "sessionId"],
    ["metadata", "session_id"],
    ["context", "sessionID"],
    ["context", "sessionId"],
    ["context", "session_id"],
];
const OPENCODE_TEXT_PATHS = [
    ["part", "text"],
    ["text"],
    ["content"],
    ["message"],
    ["delta", "text"],
    ["data", "text"],
    ["data", "content"],
    ["payload", "text"],
    ["payload", "content"],
];
const OPENCODE_ERROR_MESSAGE_PATHS = [
    ["error", "message"],
    ["error", "text"],
    ["error", "data", "message"],
    ["error", "data", "error"],
    ["error", "cause", "message"],
    ["error"],
    ["errors", "0", "message"],
    ["message"],
    ["data", "error", "message"],
    ["data", "error", "text"],
    ["data", "error", "data", "message"],
    ["data", "error"],
    ["data", "message"],
];
export function parseOpenCodeOutputLine(line) {
    const normalizedLine = line.trim();
    if (!normalizedLine) {
        return null;
    }
    let parsed;
    try {
        parsed = JSON.parse(normalizedLine);
    }
    catch {
        return null;
    }
    const record = parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : null;
    if (!record) {
        return null;
    }
    const isErrorResult = resolveIsErrorResult(record);
    const errorMessage = resolveErrorMessage(record, isErrorResult);
    const resultText = resolveResultText(record, isErrorResult);
    const sessionId = resolveSessionId(record);
    if (!errorMessage && resultText === null && sessionId === null) {
        return null;
    }
    return {
        errorMessage,
        isErrorResult,
        resultText,
        sessionId,
        terminal: errorMessage !== null,
    };
}
export function parseOpenCodeOutputDocument(text) {
    const normalizedText = text.trim();
    if (!normalizedText) {
        return null;
    }
    const lines = normalizedText
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
    if (lines.length === 0) {
        return null;
    }
    let outputText = "";
    let sessionId = null;
    let errorMessage = null;
    let isErrorResult = null;
    let parsedAnyLine = false;
    for (const line of lines) {
        const parsedLine = parseOpenCodeOutputLine(line);
        if (!parsedLine) {
            continue;
        }
        parsedAnyLine = true;
        if (parsedLine.sessionId) {
            sessionId = parsedLine.sessionId;
        }
        if (parsedLine.resultText) {
            outputText += parsedLine.resultText;
        }
        if (parsedLine.errorMessage) {
            errorMessage = parsedLine.errorMessage;
        }
        if (parsedLine.isErrorResult !== null) {
            isErrorResult = parsedLine.isErrorResult;
        }
    }
    if (!parsedAnyLine) {
        return null;
    }
    return {
        errorMessage,
        isErrorResult,
        outputText: normalizeOptionalText(outputText),
        sessionId,
    };
}
function resolveSessionId(record) {
    return resolveStringByPaths(record, OPENCODE_SESSION_ID_PATHS);
}
function resolveResultText(record, isErrorResult) {
    if (isErrorResult === true) {
        return null;
    }
    if (record.type !== "text") {
        return null;
    }
    return resolveTextByPaths(record, OPENCODE_TEXT_PATHS);
}
function resolveErrorMessage(record, isErrorResult) {
    const fromKnownPaths = resolveStringByPaths(record, OPENCODE_ERROR_MESSAGE_PATHS);
    if (fromKnownPaths &&
        (isErrorResult === true || looksLikeErrorSignal(fromKnownPaths))) {
        return fromKnownPaths;
    }
    return null;
}
function resolveIsErrorResult(record) {
    if (record.type === "error") {
        return true;
    }
    if (typeof record.is_error === "boolean") {
        return record.is_error;
    }
    if (typeof record.isError === "boolean") {
        return record.isError;
    }
    if (typeof record.ok === "boolean") {
        return !record.ok;
    }
    if (typeof record.success === "boolean") {
        return !record.success;
    }
    const status = normalizeOptionalText(record.status)?.toLowerCase();
    if (status === "error" || status === "failed" || status === "fail") {
        return true;
    }
    if (status === "ok" || status === "success" || status === "completed") {
        return false;
    }
    return null;
}
function resolveStringByPaths(record, paths) {
    for (const path of paths) {
        let current = record;
        for (const segment of path) {
            if (current && typeof current === "object" && !Array.isArray(current)) {
                current = current[segment];
                continue;
            }
            current = undefined;
            break;
        }
        const normalized = normalizeScalarText(current);
        if (normalized) {
            return normalized;
        }
    }
    return null;
}
function resolveTextByPaths(record, paths) {
    for (const path of paths) {
        let current = record;
        for (const segment of path) {
            if (current && typeof current === "object" && !Array.isArray(current)) {
                current = current[segment];
                continue;
            }
            current = undefined;
            break;
        }
        if (typeof current === "string" && current.length > 0) {
            return current;
        }
    }
    return null;
}
function looksLikeErrorSignal(value) {
    const lowered = value.toLowerCase();
    return (lowered.includes("error") ||
        lowered.includes("failed") ||
        looksLikeRuntimePermissionDenialMessage(value) ||
        lowered.includes("unauthorized") ||
        lowered.includes("not logged in") ||
        lowered.includes("invalid") ||
        lowered.includes("quota") ||
        lowered.includes("rate") ||
        lowered.includes("unsupported model") ||
        lowered.includes("model is not supported"));
}
function normalizeOptionalText(value) {
    const normalized = String(value ?? "").trim();
    return normalized.length > 0 ? normalized : null;
}
function normalizeScalarText(value) {
    if (typeof value !== "string" &&
        typeof value !== "number" &&
        typeof value !== "boolean") {
        return null;
    }
    return normalizeOptionalText(value);
}
